/**
 * Static widget configuration — localized. Returns the full widget prop bundle
 * for a given locale so the floating (landing) and docked (dashboard) loaders
 * stay in sync. Brand/theme/API stay global; copy + suggested prompts + the
 * `language` hint passed to the backend swap per locale.
 *
 * Add a new locale by adding a new entry to `WIDGET_COPY` — the `Locale` type
 * from `@/lib/i18n` is the single source of truth for the 5 supported langs.
 */
import type { Locale } from '@/lib/i18n';

export const CALLCENTER_API_URL =
  process.env.NEXT_PUBLIC_CALLCENTER_API_URL ??
  'https://callcenter-api-production-c85f.up.railway.app';

export interface WidgetCopy {
  title: string;
  subtitle: string;
  greetingHtml: string;
  composerPlaceholder: string;
  suggestedQuestions: readonly string[];
  escalationLabels: {
    title: string;
    namePlaceholder: string;
    emailPlaceholder: string;
    messagePlaceholder: string;
    submitCta: string;
    submitting: string;
    errorPrefix: string;
  };
  /** `{email}` is replaced with the actual user email when rendering. */
  contactHintTemplate: string;
}

const WIDGET_COPY: Record<Locale, WidgetCopy> = {
  es: {
    title: 'EZDocuAI',
    subtitle: 'Asistente · respondemos al instante',
    greetingHtml:
      "<p><strong>Hola 👋</strong></p><p>Soy el asistente de EZDocuAI. Pregúntame por precios, tiempos, formatos aceptados o lo que necesites del producto. También puedes adjuntar un documento o imagen con el clip.</p>",
    composerPlaceholder: 'Pregunta, busca, o adjunta un archivo…',
    suggestedQuestions: [
      '¿Cuánto cuesta?',
      '¿En cuánto tiempo la reciben?',
      '¿Qué formatos de archivo aceptan?',
      '¿Qué pasa si necesito revisiones?',
      '¿Es válido para la embajada?',
    ],
    escalationLabels: {
      title: 'Crear ticket',
      namePlaceholder: 'Nombre',
      emailPlaceholder: 'Email',
      messagePlaceholder: '¿En qué podemos ayudarte?',
      submitCta: 'Crear ticket',
      submitting: 'Creando…',
      errorPrefix: 'No pude crear el ticket',
    },
    contactHintTemplate: 'Te contactaremos a {email}',
  },
  en: {
    title: 'EZDocuAI',
    subtitle: 'Assistant · instant answers',
    greetingHtml:
      "<p><strong>Hi 👋</strong></p><p>I'm the EZDocuAI assistant. Ask me about pricing, turnaround, accepted formats or anything about the product. You can also attach a document or image with the paperclip.</p>",
    composerPlaceholder: 'Ask, search, or attach a file…',
    suggestedQuestions: [
      'How much does it cost?',
      'How long does a translation take?',
      'What file formats do you accept?',
      'What if I need revisions?',
      'Is it valid for the embassy?',
    ],
    escalationLabels: {
      title: 'Open a ticket',
      namePlaceholder: 'Name',
      emailPlaceholder: 'Email',
      messagePlaceholder: 'How can we help you?',
      submitCta: 'Open ticket',
      submitting: 'Submitting…',
      errorPrefix: 'Could not open the ticket',
    },
    contactHintTemplate: "We'll reach out at {email}",
  },
  pt: {
    title: 'EZDocuAI',
    subtitle: 'Assistente · respostas instantâneas',
    greetingHtml:
      "<p><strong>Olá 👋</strong></p><p>Sou o assistente do EZDocuAI. Pergunte sobre preços, prazos, formatos aceitos ou o que precisar sobre o produto. Você também pode anexar um documento ou imagem pelo clipe.</p>",
    composerPlaceholder: 'Pergunte, busque ou anexe um arquivo…',
    suggestedQuestions: [
      'Quanto custa?',
      'Em quanto tempo recebo a tradução?',
      'Quais formatos de arquivo vocês aceitam?',
      'E se eu precisar de revisões?',
      'É válido para a embaixada?',
    ],
    escalationLabels: {
      title: 'Abrir ticket',
      namePlaceholder: 'Nome',
      emailPlaceholder: 'E-mail',
      messagePlaceholder: 'Como podemos ajudar?',
      submitCta: 'Abrir ticket',
      submitting: 'Enviando…',
      errorPrefix: 'Não consegui abrir o ticket',
    },
    contactHintTemplate: 'Entraremos em contato em {email}',
  },
  de: {
    title: 'EZDocuAI',
    subtitle: 'Assistent · sofortige Antworten',
    greetingHtml:
      "<p><strong>Hallo 👋</strong></p><p>Ich bin der EZDocuAI-Assistent. Fragen Sie mich nach Preisen, Bearbeitungszeiten, akzeptierten Formaten oder was Sie zum Produkt wissen möchten. Sie können auch ein Dokument oder Bild per Büroklammer anhängen.</p>",
    composerPlaceholder: 'Fragen, suchen oder Datei anhängen…',
    suggestedQuestions: [
      'Was kostet es?',
      'Wie lange dauert eine Übersetzung?',
      'Welche Dateiformate werden akzeptiert?',
      'Was ist, wenn ich Änderungen brauche?',
      'Ist es für die Botschaft gültig?',
    ],
    escalationLabels: {
      title: 'Ticket eröffnen',
      namePlaceholder: 'Name',
      emailPlaceholder: 'E-Mail',
      messagePlaceholder: 'Wie können wir Ihnen helfen?',
      submitCta: 'Ticket eröffnen',
      submitting: 'Wird gesendet…',
      errorPrefix: 'Ticket konnte nicht eröffnet werden',
    },
    contactHintTemplate: 'Wir melden uns unter {email}',
  },
  fr: {
    title: 'EZDocuAI',
    subtitle: 'Assistant · réponses instantanées',
    greetingHtml:
      "<p><strong>Bonjour 👋</strong></p><p>Je suis l'assistant EZDocuAI. Posez vos questions sur les prix, les délais, les formats acceptés ou tout ce qui concerne le produit. Vous pouvez aussi joindre un document ou une image avec le trombone.</p>",
    composerPlaceholder: 'Posez une question, cherchez ou joignez un fichier…',
    suggestedQuestions: [
      'Combien ça coûte ?',
      'En combien de temps je reçois la traduction ?',
      'Quels formats de fichier acceptez-vous ?',
      'Et si j\'ai besoin de révisions ?',
      'Est-ce valable pour l\'ambassade ?',
    ],
    escalationLabels: {
      title: 'Ouvrir un ticket',
      namePlaceholder: 'Nom',
      emailPlaceholder: 'E-mail',
      messagePlaceholder: 'Comment pouvons-nous vous aider ?',
      submitCta: 'Ouvrir le ticket',
      submitting: 'Envoi…',
      errorPrefix: "Impossible d'ouvrir le ticket",
    },
    contactHintTemplate: 'Nous vous contacterons à {email}',
  },
};

export interface WidgetConfig extends WidgetCopy {
  apiUrl: string;
  product: 'ezdocu';
  tenantSlug: string;
  /** Language hint forwarded to the backend AI. */
  language: Locale;
  brandAvatarUrl: string;
  theme: { primaryColor: string; primaryGradientEnd: string };
}

export function getWidgetConfig(locale: Locale): WidgetConfig {
  const copy = WIDGET_COPY[locale] ?? WIDGET_COPY.en;
  return {
    apiUrl: CALLCENTER_API_URL,
    product: 'ezdocu',
    tenantSlug: 'public-ezdocu',
    language: locale,
    brandAvatarUrl: '/logo.png',
    theme: {
      primaryColor: '#9333ea',
      primaryGradientEnd: '#9333ea',
    },
    ...copy,
  };
}

export function renderContactHint(template: string, email: string): string {
  return template.replace('{email}', email);
}
